import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  inject,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatChip, MatChipListbox } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { Subject, fromEvent } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

import { Team } from '@core/api';

import { TeamChipColorDirective } from '../../directives/team-chip-color.directive';

@Component({
  selector: 'la-team-chips',
  templateUrl: './team-chips.component.html',
  styleUrls: ['./team-chips.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, MatChip, MatChipListbox, MatMenuModule, MatIconModule, MatButtonModule, TeamChipColorDirective],
})
export class TeamChipsComponent implements AfterViewInit, OnDestroy, OnChanges {
  @Input() teams: Team[] = [];
  @Input() showRole?: boolean;

  public teamsToRender: Team[] = [];
  public teamsForMore: Team[] = [];

  private destroy$ = new Subject<void>();

  private readonly el = inject(ElementRef);
  private readonly cd = inject(ChangeDetectorRef);

  ngAfterViewInit() {
    setTimeout(() => this.detect());

    fromEvent(window, 'resize')
      .pipe(takeUntil(this.destroy$), debounceTime(100))
      .subscribe(() => this.detect());
  }

  ngOnChanges() {
    this.detect();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  trackByTeam(team: Team) {
    return `${team.id}${team.color}`;
  }

  detect() {
    const containerWidth = this.el.nativeElement.clientWidth - 70;
    let calculatedWidth = 0;

    this.teamsToRender = [];
    this.teamsForMore = [];

    this.teams.forEach((team) => {
      const teamWidth = this.computeWidth(this.showRole ? team.name + ` (${team.role})` : team.name) + 38;
      calculatedWidth = calculatedWidth + teamWidth;

      if (containerWidth > calculatedWidth) {
        this.teamsToRender.push(team);
      } else {
        this.teamsForMore.push(team);
      }
    });

    this.cd.markForCheck();
  }

  computeWidth(text: string) {
    const div = document.createElement('div');
    div.style.display = 'inline-block';
    div.style.font = 'var(--mat-sys-label-medium)';
    div.innerHTML = text;

    document.body.appendChild(div);
    const width = div.clientWidth;
    if (div.parentNode) {
      div.parentNode.removeChild(div);
    }

    return width;
  }
}
